import LandingFooter from '@/components/LandingFooter.tsx'
import LandingHeader from '@/components/LandingHeader.tsx'
import styles from '@/pages/PolicyPage.module.scss'
import { languageBasedOnTimezone } from '@/utils/language.ts'
import { useEffect } from 'react'

export const TermsOfServicePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <>
      <div className={styles.policyContainer}>
        <div className={styles.topSection}>
          <LandingHeader language={languageBasedOnTimezone()} selectLanguageEnabled={false} />
        </div>
        <div className={styles.contentsSection}>
          <div className={styles.titleContainer}>
            <h1>TRACKER BOOT AGREEMENT(TERMS OF SERVICE)</h1>
            <div className={styles.dates}>Effective Date: February 25, 2025</div>
            <div className={styles.dates}>Last Updated: April 24, 2025</div>
            <p>
              Tracker Boot is an agile project management platform operated by Bekind Labs LLC (“Bekind Labs”)
              (collectively referred to as “we,” “us,” or “our”). By accessing or using any services available at
              <a href="https://trackerboot.com">&nbsp;https://trackerboot.com</a> (the “Application”) (collectively
              referred to as the “Service”), you acknowledge and agree to abide by the terms outlined in the Tracker
              Boot Agreement (the “Agreement”) between you and us. This Agreement also incorporates the Terms of Use,
              available at
              <a href="https://trackerboot.com/policies/terms-of-use">
                &nbsp;https://trackerboot.com/policies/terms-of-use
              </a>
              (“Terms”), which Bekind Labs may update from time to time and are incorporated herein by reference. If you
              do not agree to the terms of this Tracker Boot Agreement, you must refrain from using or accessing the
              Service. If you are entering into this Agreement on behalf of an organization rather than as an
              individual, you confirm that you have the legal authority to bind that organization. By proceeding, you
              officially enter into this Agreement on behalf of the organization. The terms “you” or “your” refer to
              both the entity accepting this Tracker Boot Agreement and the authorized individual agreeing to these
              terms on the entity’s behalf.
            </p>
          </div>
          <div className={styles.contentsContainer}>
            <h2>1. Definitions</h2>
            <ul>
              <li>
                <span>1.1. "Customer"</span> refers to the organization or individual that subscribes to the Tracker
                Boot Service and enters into the Agreement with Bekind Labs, and may manage one or more Accounts and
                invite Authorized Users to collaborate on Projects.
              </li>
              <li>
                <span>1.2. "Authorized Users"</span> refers to individuals invited by the Customer to access and use the
                Service, including employees, contractors, or agents who operate under the Customer’s account and in
                accordance with the Customer’s instructions.
              </li>
              <li>
                <span>1.3 “Organization”</span>signifies the entity that has agreed to and accepted the terms of this
                Tracker Boot Agreement.
              </li>
              <li>
                <span>1.4 “User”</span>indicates an Organization or individual that subscribes to a free-tier version of
                the Service.
              </li>
              <li>
                <span>1.5 “Paid User”</span>means an Organization or individual that subscribes to a paid version of the
                Service.
              </li>
              <li>
                <span>1.6 “Account”</span>refers to a collection of Projects linked to a specific payment Plan, managed
                by a single Account Owner and able to include multiple Projects and Project Members, as permitted by the
                selected Plan. Both Paid Users and free-tier Users are assigned an Account upon registration.
              </li>
              <li>
                <span>1.7 “Account Owner”</span>denotes the individual or representative of an Organization responsible
                for overseeing payments and billing for a given Account.
              </li>
              <li>
                <span>1.8 “Plan”</span>describes the subscription level chosen by the Account Owner from the available
                options on the Plans and Billing Page.
              </li>
              <li>
                <span>1.9 “Plans and Billing Page”</span>refers to the webpage displaying the descriptions and pricing
                of the Service, which may be updated periodically.
              </li>
              <li>
                <span>1.10 “Subscription Fees”</span>represent the amount paid to Bekind Labs for the Service for a
                defined Subscription Term.
              </li>
              <li>
                <span>1.11 “Subscription Term”</span>specifies the time period during which access to the Service is
                granted, as specified when the Customer registers for the Service.
              </li>
              <li>
                <span>1.12 “Evaluation Trial”</span>refers to the initial free trial automatically applied when an
                Account is first created.
              </li>
              <li>
                <span>1.13 “Project”</span>defines a workspace where information is organized and managed in the form of
                user stories, with controlled access to specific individuals or the public (for read-only access),
                depending on project settings.
              </li>
              <li>
                <span>1.14 “Project Member”</span>is a specific individual, identified by their UserID, who has been
                granted explicit access to a Project.
              </li>
              <li>
                <span>1.15 “UserID”</span>signifies the email address linked to a provider and the username used to
                identify a User Profile.
              </li>
              <li>
                <span>1.16 “User Profile”</span>means personal profile information such as username, full name, email,
                and other Login Credentials, which users provide and update as necessary.
              </li>
              <li>
                <span>1.17 “Login Credentials”</span>refer to UserID, passwords, API tokens, or other security
                credentials that enable access to the Service.
              </li>
              <li>
                <span>1.18 “Relationship Data”</span>comprises personal details collected during the registration,
                activation, and maintenance of user profiles and/or accounts, such as names and contact details of
                individuals involved in maintaining or using the Service, but does not include information collected
                through publicly accessible portions of our websites governed by our Privacy Policy at
                https://www.bekindlabs.com/privacy-policy.
              </li>
              <li>
                <span>1.19 “Customer Personal Data”</span>refers to personal data submitted or uploaded by the Customer
                or its Users as part of project content, which may include user stories, tasks, comments, file
                attachments, labels, custom fields, and any personal data embedded within project content, and which
                Bekind Labs does not control or monitor but processes solely as a data processor.
              </li>
              <li>
                <span>1.20 “Tracker Boot Account Data”</span>refers to personal information provided by the Customer or
                its Users during account creation, profile setup, or project invitations, including full name, email
                address (UserID), username, avatar URL, organization name, login credentials, API tokens, and billing
                information.
              </li>
              <li>
                <span>1.21 “Usage Data”</span>refers to analytical and operational metadata generated through use of the
                Service, including but not limited to feature interaction logs, event timestamps, API usage metrics,
                number of collaborators and projects, number of projects a user is part of, project size and storage
                consumption, and system performance or diagnostics information.
              </li>
              <li>
                <span>1.22 “Sensitive Data”</span>refers to any data revealing racial or ethnic origin, political
                opinions, religious or philosophical beliefs, trade union membership, genetic or biometric data, health
                information, sexual orientation, or other sensitive categories as defined under Applicable Data
                Protection Laws, which Bekind Labs does not intentionally collect.
              </li>
              <li>
                <span>1.23 “Confidential Information”</span>encompasses data, software, and materials provided by Bekind
                Labs to you, including but not limited to login credentials, authentication tokens, API keys,
                proprietary software, diagnostic logs, business and strategic information such as custom pricing models
                or contractual terms, billing and payment contact details, and any third-party confidential data, as
                well as technical documentation and sensitive system metadata such as performance or usage metrics that
                are explicitly marked as “confidential” or should reasonably be understood as such, excluding any
                information generally available to the public or made publicly accessible by Bekind Labs; details on how
                Bekind Labs handles such data can be found in the Sub-processor list and Security measures
                documentation.
              </li>
              <li>
                <span>1.24 “Content”</span>includes any and all applications, files, information, data, software, or
                other content uploaded, published, or displayed by users via the Service, including but not limited to
                project data, documents, images, plugins, API integrations, and custom scripts.
              </li>
              <li>
                <span>1.25 “Your Content”</span>represents any data, files, or other materials shared, displayed, or
                stored through the Service by you or authorized users of your account, including Relationship Data but
                excluding Usage Data.
              </li>
              <li>
                <span>1.26 “Service Descriptions”</span>refer to any description of or information that details the
                Service available through this site, including any usage guidelines and access instructions, which may
                be updated periodically.
              </li>
              <li>
                <span>1.27 “Third-party content”</span>refers to third-party data, Content, services, or applications,
                including open-source software.
              </li>
              <li>
                <span>1.28 “Third-Party Terms”</span>refer to the most up-to-date terms and conditions that apply to any
                Third-Party Content.
              </li>
            </ul>
          </div>
          <div className={styles.contentsContainer}>
            <h2>2. The Service</h2>
            <ul>
              <li>
                <span>2.1. General Terms.</span>All-access to and use of any Service are subject to the terms and
                conditions of this Tracker Boot Agreement. We may provide the Service to you with the assistance of our
                affiliates, corporate partners, third-party service providers, licensors, and other business
                collaborators. By using the Service, you agree to comply with all applicable laws, rules, and
                regulations, as well as any relevant third-party terms and Service Descriptions.
              </li>
              <li>
                <span>2.2. Access to the Service.</span>You may access and use the Service exclusively for your personal
                or organizational benefit. To gain access to the Service, You must register for the Service and set up
                an authorized User Profile. It is your responsibility to keep your Login Credentials secure and User
                Profile accurate, complete, and current as long as you continue to use the Service. You agree and
                acknowledge that each User is assigned unique Login Credentials and that Login Credentials may only be
                used by one (1) individual. Login Credentials cannot be shared. You are responsible for any use that
                occurs under your Login Credentials. If unauthorized access is suspected, you must notify us
                immediately. Neither we nor our affiliates are responsible for any unauthorized access to or use of your
                account.
              </li>
              <li>
                <span>2.3 Subscription Plans and Upgrades.</span>Users can choose to upgrade to a paid plan at any time.
                Account Owners who are subscribed to a paid plan may increase their plan level by paying the required
                additional license fees, as set forth on the Account’s Plans and Billing Page.
              </li>
              <li>
                <span>2.4. Account Management.</span>When you register for Tracker Boot, Bekind Labs will create an
                Account for you. When you become a User by accepting an invitation to a Project or Account, you may
                create an Account. Users may create multiple Accounts. The Account Owner is the primary representative
                responsible for managing the subscription unless they assign another representative as the new Account
                Owner. You may also designate other representatives as Account Administrators to help manage your
                Account. You are responsible for all activity that occurs under your Account. Each Account is tied to a
                specific Plan, and Bekind Labs reserves the right to modify plan structures at its discretion. To
                collaborate, you may create one or more Projects within the Service. Each Project belongs to a single
                Account and can include multiple Project Members. You shall notify Bekind Labs immediately of any
                unauthorized use of any password, account, copying, or access to the Service. Each Customer is assigned
                a unique UserID for identification.
              </li>
              <li>
                <span>2.5. Third-Party Content.</span>As part of your use of the Service, you may be provided with
                access to Third Party Content or integrations, which may be subject to separate terms, conditions,
                and/or fees. Such content is provided on an “AS-IS” basis, without indemnification or support, and
                Bekind Labs disclaims all warranties of any kind, whether express or implied, including but not limited
                to warranties of merchantability, fitness for a particular purpose, and non-infringement. You are solely
                responsible for reviewing, accepting, and complying with any applicable Third-Party Terms and
                restrictions related to the Third-Party Content. All Third Party Terms are solely between you and the
                applicable third party. Bekind Labs does not endorse, manage, or assume any liability for third-party
                services. Your use of any Third-Party Content is entirely at your own risk, and Bekind Labs is not
                liable for any issues, damages, losses, or claims arising from or related to your use of any Third-Party
                Content.
              </li>
              <li>
                <span>2.6. Open-source Licenses</span> Tracker Boot uses open-source software components licensed under
                widely adopted, permissive licenses such as MIT and Apache 2.0. While these licenses do not require
                public distribution of modified source code, we remain committed to transparency. For a full list of
                open-source components used in Tracker Boot and their respective licenses, please visit
                https://trackerboot.com/dependencies.html
              </li>
              <li>
                <span>2.7. Relationship Data</span>We may collect and process Relationship Data in connection with our
                Service. By using the Service, you acknowledge and consent to our ability to utilize and disclose
                Relationship Data for purposes such as managing your account, sending important updates, providing
                services or relevant information, processing payments for subscriptions, enhancing and developing new
                features, ensuring regulatory compliance, and offering customer support. Additionally, we may share
                Relationship Data with our affiliates and trusted third-party service providers who assist with
                essential functions, such as payment processing and infrastructure support, as necessary to maintain and
                improve the Service. All such processing and sharing of Relationship Data will be conducted in
                accordance with applicable laws and regulations.
              </li>
              <li>
                <span>2.8. Usage Data</span>We may generate, collect, store, and utilize Usage Data for various
                operational, analytical, and service enhancement purposes. You agree that, between you and Bekind Labs,
                Bekind Labs holds ownership of all such Usage Data. For instance, we use Usage Data to monitor and
                manage infrastructure, network storage, and system performance, facilitate billing processes and
                capacity planning, support software license tracking and ensure compliance reporting, and enhance and
                develop new product features and services. Additionally, we may share Usage Data with our affiliates and
                select third-party partners to fulfill contractual obligations, such as tracking software license usage
                and compliance reporting. You acknowledge and consent to Bekind Labs’ ability to process, analyze, and
                operate on Relationship Data, Usage Data, and Your Content as required to deliver and improve the
                Service you have subscribed to under this Tracker Boot Agreement. Where permitted by applicable laws, we
                may aggregate Usage Data in a manner that removes direct customer identification (“Aggregate Data”). We
                retain the right to use and disclose Aggregate Data for any lawful purpose. We retain the right to use
                and disclose Aggregate Data for any lawful purpose.
              </li>
              <li>
                <span>2.9. Data Disclosure:</span> You agree that Bekind Labs may disclose Your Content, Relationship
                Data, and Usage Data under certain circumstances.
              </li>
              <ul>
                <li>
                  2.9.1. These disclosures may occur with our affiliates and other entities within the Bekind Labs
                  corporate group on a global scale for purposes outlined in this agreement.
                </li>
                <li>
                  2.9.2. We may share such data with trusted third-party service providers who assist with core
                  operational functions, including but not limited to hosting, data analytics, payment processing, order
                  fulfillment, IT infrastructure, customer support, email communications, fraud prevention, credit card
                  processing, and auditing.
                </li>
                <li>
                  2.9.3. In cases of corporate restructuring, such as mergers, acquisitions, sales, joint ventures,
                  assignments, or transfers of assets, your data may be transferred as part of the business transition.
                  This also applies in scenarios involving insolvency or bankruptcy proceedings where such disclosure is
                  necessary.
                </li>
                <li>2.9.4. Bekind Labs may disclose data as:</li>
                <ul>
                  <li>2.9.4.1. to required or permitted by law, including laws outside your country of residence;</li>
                  <li>2.9.4.2. to comply with legal processes;</li>
                  <li>
                    2.9.4.3. to respond to governmental or public authority requests, including public and government
                    authorities outside your country of residence;
                  </li>
                  <li>2.9.4.4. to enforce this Tracker Boot Agreement;</li>
                  <li>2.9.4.5. to protect Bekind Labs’ operations or those of any of its affiliates;</li>
                  <li>
                    2.9.4.6. to protect Bekind Labs’ rights, privacy, safety, and property, including that of our
                    affiliates, users, and other third parties;
                  </li>
                  <li>2.9.4.7. to enable Bekind Labs to pursue legal remedies or mitigate potential damages.</li>
                </ul>
              </ul>
              <li>
                <span>2.10.</span> If any transfer of personal data occurs beyond the European Economic Area (<b>EEA</b>
                ) or Switzerland, it will be subject to the applicable
                <a href="https://trackerboot.com/policies/data-processing-addendum">&nbsp;Data Processing Addendum</a>,
                ensuring compliance with relevant data protection laws.
              </li>
            </ul>
          </div>
          <div className={styles.contentsContainer}>
            <h2>3. Your Content</h2>
            <ul>
              <li>
                <span>3.1. Access to Your Content</span> You are solely responsible for any content you upload, share,
                or publish through the Service (“Your Content”). Bekind Labs acts only as a passive conduit for the
                online distribution and publication of Your Content. The Service processes, stores, transmits, performs
                operations on, and distributes Your Content as needed for service functionality. You and any authorized
                users retain full ownership, rights, and control over Your Content. Bekind Labs’ access and usage rights
                are strictly limited to those explicitly granted under this Tracker Boot Agreement, and no additional
                rights over Your Content are implied. In accordance with applicable data privacy laws, Bekind Labs acts
                as a data processor on your behalf, while you, as the data controller, maintain responsibility for Your
                Content to the extent it constitutes personal data under applicable laws.
              </li>
              <li>
                <span>3.2. Security of Your Content</span>You are responsible for protecting the security of Your
                Content, including any access you might provide to Your Content by your employees, customers, or other
                third parties. You must properly configure and use the Service so that it is suitable for your use. It
                is your duty to protect the privacy of any of your user’s data, including implementing and maintaining a
                privacy policy that complies with applicable legal requirements. You must notify your users of any
                necessary disclosures and obtain legally required consent before processing their data from your users
                concerning your use of the Service. You are solely responsible for complying with any laws or
                regulations that might apply to your Content. The Service is not designed nor intended for storing or
                handling data subject to the Health Insurance Portability and Accountability Act (HIPAA) or other
                similarly regulated data. If Your Content is inadvertently exposed, lost, or otherwise compromised, and
                you have not encrypted or backed up, or you have not taken necessary precautions required by the
                relevant laws or regulations to protect Your Content, you assume full liability for any resulting
                consequences. Bekind Labs will make commercially reasonable efforts to inform you of any unintended
                exposure or data loss in accordance with applicable laws, regulations, and government requests.
              </li>
              <li>
                <span>3.3. Storage and Transfer of Your Content:</span>You consent to the storage of Your Content in
                Japan or any other locations where Bekind Labs maintains data centers, as determined at our discretion.
                By uploading Your Content to the Service, you acknowledge and agree that it may be transferred,
                accessed, and processed globally, including to and from Japan. If you provide Your Content in connection
                with customer support or other service-related functions, you authorize Bekind Labs to process it in any
                country where we or our service providers operate. You are solely responsible for ensuring compliance
                with all applicable laws when transferring data across jurisdictions.
              </li>
            </ul>
          </div>
          <div className={styles.contentsContainer}>
            <h2>4. Acceptable Use</h2>
            <ul>
              <li>
                <span>4.1. Content Restrictions:</span> You will not, and you will take steps to ensure that your
                authorized users and third-party users who access any service you provide through the Service do not
                post Content that:
              </li>
              <ul>
                <li>
                  4.1.1. Poses a risk of harm, loss, physical or mental injury, emotional distress, death, disability,
                  disfigurement, or physical or mental illness to an authorized user or any other person or entity;
                </li>
                <li>4.1.2. Creates a risk of any other loss or damage to any person or property;</li>
                <li>
                  4.1.3. Constitutes, contributes to, or facilitates any form of illegal activity, including crimes or
                  civil violations;
                </li>
                <li>
                  4.1.4. Contains any information or material that is unlawful, harmful, abusive, harassing, offensive
                  (racially, ethnically, or otherwise), misleading, defamatory, infringing, invasive of personal privacy
                  or publicity rights, humiliating to others (publicly or otherwise), libelous, exploitative,
                  threatening, or otherwise objectionable and inappropriate;
                </li>
                <li>4.1.5. Contains any information or content that is illegal;</li>
                <li>
                  4.1.6. Contains any information or content that infringes upon the rights of others, including but not
                  limited to intellectual property rights, publicity rights, and personal privacy rights, or that you do
                  not have the legal authority to disclose due to applicable laws, contractual obligations, or fiduciary
                  duties.
                </li>
              </ul>
              <li>
                <span>4.2. User Responsibilities</span>By using the Service, you confirm that Your Content does not
                violate any third-party rights, including intellectual property, privacy, or publicity rights. You are
                responsible for ensuring that your use of the Service complies with your privacy policies and all
                applicable local, national, and international laws and regulations, including any required encryption or
                data protection standards.
              </li>
              <li>
                <span>4.3 Age Requirements</span>The Service is intended for individuals aged 16 and above. Users under
                16 may only access the Service with the verifiable consent of a parent or legal guardian, or under the
                supervision of an authorized educational institution. Bekind Labs reserves the right to suspend or
                delete accounts found in violation of this requirement.
              </li>
              <li>
                <span>4.4. Enforcement of Acceptable Use</span>If you become aware that Your Content—or that of an
                authorized user—violates this Agreement, you are required to take immediate action, including suspending
                or removing the applicable Content or restricting the end user’s access. Should you fail to do so,
                Bekind Labs reserves the right to intervene by requesting corrective action. If you do not comply with
                this request, we may suspend your account, restrict access to certain features, or remove the offending
                content until you comply with our request and the issue is resolved.
              </li>
            </ul>
          </div>
          <div className={styles.contentsContainer}>
            <h2>5. Ownership of Service and Rights to Feedback</h2>
            <ul>
              <li>
                <span>5.1. Ownership of the Service</span>Bekind Labs, along with its licensors, exclusively owns and
                retains all rights, titles, and interests in and to the Service. This includes but is not limited to any
                improvements, enhancements, modifications, and derivative works associated with the Service, as well as
                all related intellectual property rights. Furthermore, Bekind Labs reserves all rights over any
                information, data, analytics, or insights that we collect and analyze in connection with the Service,
                such as user behavior patterns, feature usage statistics, and user feedback, which help us refine,
                enhance, and optimize the Service. Your use of the Service is strictly limited to the rights expressly
                granted in this Agreement. No additional rights regarding the Service, its intellectual property, or
                related materials are implied.
              </li>
              <li>
                <span>5.2. User Content and Limited License</span>You retain ownership of all content, data, and
                materials you or your Authorized Users upload to the Service (“Your Content”). By using the Service, you
                grant Bekind Labs a non-exclusive, worldwide, royalty-free license to host, store, reproduce, and
                process Your Content solely for the purpose of providing, supporting, and improving the Service. Bekind
                Labs acts as a data processor for Your Content and will not access, use, or share such content except as
                necessary to provide the Service or as required by law.
              </li>
              <li>
                <span>5.3. Responsibility for Your Content</span>You are solely responsible for the legality, accuracy,
                and integrity of Your Content. Bekind Labs does not control or monitor Your Content and acts only as a
                passive conduit for its transmission and storage. You represent and warrant that you have all necessary
                rights and permissions to use and share Your Content via the Service.
              </li>
              <li>
                <span>5.4. Feedback Contributions</span>By providing suggestions, comments, or feedback (
                <b>“Feedback”</b>) regarding the Service, including ideas for improvements or enhancements, you agree
                that we may use such Feedback for any purpose without restriction. You grant us a non-exclusive,
                perpetual, irrevocable, royalty-free, transferable, worldwide license, including the right to
                sublicense, use, reproduce, perform, display, disclose, distribute, modify, create derivative works of,
                and otherwise utilize the Feedback in any form, whether known now or conceived in the future. This
                includes the right to make, use, sell, offer to sell, import, and export any product or service
                incorporating or derived in whole or in part from the Feedback. By submitting Feedback, you acknowledge
                that Bekind Labs has sole discretion to act upon, integrate, or disregard any suggestions provided.
              </li>
            </ul>
          </div>
          <div className={styles.contentsContainer}>
            <h2>6. Payment and Taxes</h2>
            <ul>
              <li>
                <span>6.1. Evaluation Trial</span>
              </li>
              <ul>
                <li>
                  6.1.1. During the Evaluation Trial, you may create and manage an unlimited number of Projects and
                  Project Members within your Account, with full access to all features of the Service. No credit card
                  is required for participation in the Evaluation Trial. At the conclusion of the Evaluation Trial, if
                  you do not upgrade to a paid Plan, your Account will automatically transition to the free Plan as
                  outlined on the Plans and Billing Page. If your Account exceeds the limitations of the free Plan, it
                  will be placed in a read-only mode until either (a) the number of Projects and Project Members is
                  reduced to fit within the free Plan limits or (b) you choose to upgrade to a paid Plan that
                  accommodates your Account’s usage. You may upgrade to a paid Plan at any time during the Evaluation
                  Trial; however, Bekind Labs will not charge you until the trial period ends unless otherwise stated on
                  the Plans and Billing Page.
                </li>
                <li>
                  6.1.2. Each user is eligible for only one Evaluation Trial per Account. If you create additional
                  Accounts, they will automatically begin on the free Plan without an Evaluation Trial. Projects cannot
                  be transferred between Accounts that are currently under an Evaluation Trial. If you represent an
                  Organization, only one designated representative of that Organization is permitted to claim an
                  Evaluation Trial. In cases where your Organization is enrolled in an enterprise Account based on
                  domain-level management (e.g., all emails ending with @yourcompany.com), Evaluation Trials may be
                  restricted to one per domain. In such cases, users sharing the same domain (e.g., @bekindlabs.com) may
                  not be eligible for individual trials, even if they register separate personal Accounts. Please refer
                  to your Organization’s account administrator or contact Bekind Labs for clarification.
                </li>
              </ul>
              <li>
                <span>6.2. Subscription Fees</span> The subscription fees for the Service are determined based on the
                Plan you select, as specified on the Plans and Billing Page. If a Paid User requires additional features
                beyond those included in their current Plan, the Paid User must upgrade to a higher-tier Plan that
                accommodates expanded usage or additional members. Subscription fees for such upgrades will be adjusted
                on a prorated basis for the remainder of the Subscription Term, as detailed on the Plans and Billing
                Page. You are responsible for all taxes other than taxes levied on Bekind Labs’ NET income. As Bekind
                Labs is a Japan-registered entity, all subscription fees are subject to Japanese tax laws. Subscription
                fees do not include any applicable taxes, including Japan’s Consumption Tax (JCT) or any other relevant
                sales, use, or value-added taxes. If Bekind Labs is required to collect and remit JCT, VAT, Goods and
                Services Tax (GST), or any other applicable tax in connection with a Paid User’s purchase, such taxes
                will be billed to and must be paid by the Paid User.
              </li>
              <li>
                <span>6.3. Payment Terms</span> Payment Terms Paid Users may select either an annual or monthly billing
                cycle for their Subscription Fees. The first payment is due on the date the Paid User upgrades to a paid
                Plan (the “First Upgrade Date”), with subsequent charges occurring on each monthly anniversary for those
                on a monthly billing cycle or on the yearly anniversary of the Effective Date for those on an annual
                billing cycle. The fee schedule is available at https://www.trackerboot.com/pricing, and Bekind Labs
                reserves the right to update pricing and plan structures as necessary. Bekind Labs is not obligated to
                provide access to any paid Service until all required payment and billing details have been successfully
                submitted and processed. Subscription Fees will be automatically charged to the payment method
                associated with the Paid User’s account, which may include credit card payments or invoicing, depending
                on the Plan and payment terms agreed upon. By subscribing, you agree to automatic credit card payments
                or invoice-based payments for each billing cycle applicable to your subscription until you cancel your
                Account by deleting it through the Account settings. A transaction receipt will be issued at the end of
                each billing cycle. Paid Users may upgrade from one paid Plan to another at any time during the current
                subscription term. In such cases, the Subscription Fee for the upgraded Plan will be adjusted on a
                prorated basis for the remainder of the current billing cycle, with immediate charges applied. If (a) a
                Paid User cancels or downgrades their Account, or (b) either party terminates this Agreement before the
                end of the current subscription term, Bekind Labs is not required to issue refunds, credits, or
                reimbursements for any unused portion of the subscription.
              </li>
              <li>
                <span>6.4. Taxes and Payment Obligations</span>All fees associated with the Service are exclusive of any
                applicable taxes. All fees associated with the Service are exclusive of any applicable taxes. You are
                responsible for paying or reimbursing Bekind Labs for any taxes that arise from transactions under this
                Tracker Boot Agreement. “Taxes” include, but are not limited to, sales tax, use tax, value-added tax
                (VAT), goods and services tax (GST), business and occupation taxes, gross receipts taxes, export and
                import duties, customs charges, and any other similar levies imposed by any government or regulatory
                authority, excluding taxes based on Bekind Labs’ net income. As a company registered in Japan, Bekind
                Labs complies with Japanese tax laws, including Japan’s Consumption Tax (JCT), which is currently 10%.
                Customers based in Japan will have JCT applied to their service fees and must pay this tax in addition
                to the base fees. For customers outside Japan, you are responsible for adhering to your local tax
                regulations. If required by law, Bekind Labs may collect and remit applicable Value-Added Tax (VAT),
                Goods and Services Tax (GST), sales tax, use tax or other levies imposed by foreign tax authorities.
                Business customers located outside Japan may be subject to the reverse charge mechanism, in which case
                you are responsible for reporting and paying the applicable tax directly to your local tax authority.
                You agree not to withhold any portion of payments due under this Agreement. The name and billing address
                you provide at the time of registration or in connection with your payment method will be used to
                determine your applicable tax jurisdiction. You acknowledge that Bekind Labs may rely on this
                information to comply with Japanese tax laws and other international tax obligations. If you are
                required by law to withhold taxes from your payments to Bekind Labs, you shall provide the necessary
                documentation and ensure that the full-service fee, excluding the withheld tax, is paid to Bekind Labs.
                Bekind Labs reserves the right to update its tax collection practices in response to changes in Japanese
                or international tax laws and may modify how taxes are applied based on regulatory requirements.
              </li>
            </ul>
          </div>
          <div className={styles.contentsContainer}>
            <h2>7. General Conditions for Suspension</h2>
            <ul>
              <li>
                <span>7.1. General.</span> Bekind Labs reserves the right to temporarily suspend your access to the
                Service at its sole discretion under any of the following circumstances:
              </li>
              <ul>
                <li>
                  7.1.1. Payment Delinquency: If your payment for the Service remains unpaid for fourteen (14) days past
                  the due date. Such suspension will occur after a fourteen (14)-day grace period from the original due
                  date. This suspension is intended as a temporary measure, and access may be reinstated if payment is
                  received before further escalation. Failure to resolve the delinquency within thirty (30) days after
                  suspension may result in Termination under Section 8.2.1(e).
                </li>
                <li>
                  7.1.2. Violation of Terms: If you or your account users breach this Tracker Boot Agreement, including
                  but not limited to non-compliance with the Acceptable Use Policy.
                </li>
                <li>
                  7.1.3. Failure to Comply with Requests: If you fail to respond to or act upon specific requests from
                  Bekind Labs as outlined in Sections 3, 4, or 5 of this Agreement.
                </li>
                <li>
                  7.1.4. Security or Operational Threat: If your use of the Service compromises system security,
                  disrupts service functionality, interferes with other users, causes damage, or involves unauthorized
                  access to our servers, networks, or third-party infrastructure (including but not limited to mobile
                  carriers and external service providers).
                </li>
                <li>
                  7.1.5. Legal Compliance: If we receive a lawful directive, such as a subpoena or government request,
                  requiring us to restrict or suspend your access.
                </li>
              </ul>
              <li>
                <span>7.2. Effect of Suspension</span>
              </li>
              <ul>
                <li>
                  7.2.1. You remain financially responsible for any outstanding or accrued fees, including those
                  incurred before and during the suspension period. If your access to the Service is suspended due to
                  your actions, violations, or failure to comply with these Terms, Bekind Labs is not obligated to
                  provide any refunds, credits, or reimbursements for any unused portion of your subscription or service
                  fees.
                </li>
                <li>
                  7.2.2. Access may be restored at Bekind Labs’ discretion once the issue leading to suspension has been
                  resolved to our satisfaction.
                </li>
              </ul>
            </ul>
          </div>
          <div className={styles.contentsContainer}>
            <h2>8. Subscription Term and Termination</h2>
            <ul>
              <li>
                <span>8.1. Subscription Term</span>This Tracker Boot Agreement takes effect when you first access or use
                the Service and remains in force for the duration of your Subscription Term unless terminated earlier in
                accordance with the provisions outlined herein.
              </li>
              <li>
                <span>8.2. Termination</span>
              </li>
              <ul>
                <li>
                  8.2.1. Termination for Cause. Bekind Labs may immediately terminate this Tracker Boot Agreement under
                  the following circumstances:
                </li>
                <ul>
                  <li>
                    (a) we determine, in our sole discretion, that any of the provisions of this Tracker Agreement;
                  </li>
                  <li>
                    (b) Violation of Terms: If we determine, at our sole discretion, that you have breached any part of
                    this Agreement.
                  </li>
                  <li>(c) Uncorrectable Breach: If you commit a violation that cannot be remedied.</li>
                  <li>
                    (d) Failure to Remedy a Breach: If you breach a provision that can be corrected but fail to do so
                    within thirty (30) days of receiving written notice from us.
                  </li>
                  <li>
                    (e) Payment Delinquency: If you fail to make a payment and your account remains suspended and
                    overdue for thirty (30) days following the suspension date, as initiated under Section 7.1.1.
                  </li>
                </ul>
                <li>
                  8.2.2. Termination Due to Insolvency. Bekind Labs may also terminate this Agreement immediately if
                  you:
                </li>
                <ul>
                  <li>(a) Cease operations or suspend business activities.</li>
                  <li>(b) Declare insolvency or acknowledge an inability to meet financial obligations.</li>
                  <li>(c) Enter into an assignment for the benefit of creditors.</li>
                  <li>(d) Become subject to receivership, bankruptcy, or similar legal proceedings.</li>
                </ul>
                <li>
                  8.2.3. Termination for Convenience: You may terminate this Tracker Boot Agreement at any time by
                  requesting an account deletion via email at trackerboot@bekindlabs.com.
                </li>
                <li>8.2.4. Effect of Termination. Upon termination of this Tracker Boot Agreement for any reason,</li>
                <ul>
                  <li>
                    (a) all rights granted to you under this Agreement, including access to any of Your Content stored
                    within the Service, will be immediately revoked.
                  </li>
                  <li>
                    (b) You must promptly cease all access and use of the Service and ensure that any Confidential
                    information obtained through the Service is deleted or destroyed.
                  </li>
                  <li>
                    (c) For a period of thirty (30) days following termination, Bekind Labs will retain Your Content,
                    but you will no longer have access to the Service or any stored data. During this retention period,
                    your account will remain inactive and inaccessible unless otherwise agreed in writing.
                  </li>
                  <li>
                    (d) After this period, Bekind Labs reserves the right to delete Your Content unless otherwise
                    required by law or agreed upon in writing.
                  </li>
                </ul>
                <li>
                  Certain provisions of this Agreement will survive termination, including but not limited to clauses
                  related to Free/Open Source Software, Relationship Data, Usage Data, Data Disclosure, Your Content,
                  Acceptable Use, Intellectual Property Ownership, Payment and Taxes, Termination Provisions,
                  Confidential Information, and any applicable jurisdictional terms. Termination of the Service does not
                  entitle you to refunds, credits, or compensation of any kind. You remain responsible for any
                  outstanding fees, including those for the remainder of the Subscription Term and any usage charges
                  incurred prior to termination.
                </li>
                <li>
                  8.2.5. Collection and Enforcement. If any fees owed by you remain unpaid after the due date and are
                  not resolved within the applicable grace period, Bekind Labs reserves the right to initiate collection
                  efforts. These may include, but are not limited to, suspending your access to the Service, engaging
                  third-party collection agencies, or pursuing legal action. You agree to be responsible for all
                  reasonable costs associated with such enforcement efforts, including attorney’s fees, court costs, and
                  collection agency fees.
                </li>
              </ul>
            </ul>
          </div>
          <div className={styles.contentsContainer}>
            <h2>9. General Provisions</h2>
            <ul>
              <li>
                <span>9.1. Successors and Assignments</span>This Tracker Boot Agreement may not be transferred or
                assigned without prior written consent from both parties, which shall not be unreasonably withheld,
                conditioned, or delayed. However, Bekind Labs reserves the right to assign or transfer this Tracker Boot
                Agreement, in whole or in part, without requiring your consent, to any successor entity acquiring all or
                substantially all of Bekind Labs’ business or assets through merger acquisition, reorganization, or
                asset sale, including any subsidiaries or affiliates. Any unauthorized transfer or assignment in
                violation of this clause shall be deemed null and void. Subject to these restrictions, the terms and
                conditions of this agreement shall remain binding and enforceable upon all permitted successors and
                assigns.
              </li>
              <li>
                <span>9.2. Compliance with Laws, Export Controls, and Government Regulations</span>Both parties agree to
                comply with all applicable laws, regulations, and governmental requirements related to the use and
                provision of the Service. The Service is subject to the laws of the jurisdictions where it is developed,
                operated, or provided. It is developed in Japan, the Republic of Korea, and India and offered worldwide,
                including in the United States and the European Union. As such, it is subject to the export control,
                data protection, and cybersecurity laws of multiple jurisdictions, including but not limited to Japan’s
                Foreign Exchange and Foreign Trade Act (FEFTA), the Republic of Korea’s Foreign Trade Act and Strategic
                Goods Control Regulations, India’s applicable export control and data protection laws, the United States
                Export Administration Regulations (EAR) and International Traffic in Arms Regulations (ITAR), and the
                European Union Dual-Use Regulation (Council Regulation (EC) No. 2021/821). Unauthorized export,
                re-export, transfer, or disclosure of the Service in violation of these regulations is strictly
                prohibited.
              </li>
              <li>
                By using the Service, you confirm and warrant that (a) you are not, and are not acting on behalf of, any
                individual, organization, or entity that (i) is a citizen, resident, or controlled entity of a country
                subject to trade sanctions or export restrictions imposed by Japan, the Republic of Korea, India, the
                United States, or the European Union, or (ii) appears on any restricted entity lists, including but not
                limited to the Japanese Ministry of Economy, Trade, and Industry (METI) End User List, the Korean
                Ministry of Trade, Industry, and Energy (MOTIE) export control lists, the U.S. Treasury Department’s
                Specially Designated Nationals (SDN) List, the U.S. Commerce Department’s Entity List or Denied Persons
                List, and the EU Consolidated Financial Sanctions List.
              </li>
              <li>
                You also confirm and warrant that (b) you shall not use the Service for any activities prohibited under
                Japanese, Korean, Indian, U.S., or EU laws, including but not limited to (i) the development, design,
                manufacture, or proliferation of nuclear, biological, or chemical weapons, (ii) the creation of
                missiles, military applications, or any restricted defense-related projects, and (iii) the unauthorized
                transfer of encryption technology or cybersecurity tools in violation of Japanese, Korean, Indian, U.S.,
                or EU regulations.
              </li>
              <li>
                If any portion of the Service includes software or documentation subject to government procurement
                regulations in any of these jurisdictions, such materials shall be classified as “commercial computer
                software” and “commercial computer software documentation” in accordance with applicable government
                procurement laws. Any use, reproduction, release, performance, display, or disclosure of these materials
                by government agencies shall be governed solely by this Agreement, except where explicitly permitted by
                Bekind Labs.
              </li>
              <li>
                <span>9.3. Modifications and Updates</span>Bekind Labs reserves the right to modify, update, or
                discontinue any aspect of the Service, including this Tracker Agreement, the Service Descriptions. These
                modifications may include changes to functionality, availability, pricing, or adjustments to third-party
                terms related to integrations or external content. It is your responsibility to periodically review the
                Service console for updates or modifications. We will notify you of any material changes to the Service,
                the Tracker Agreement by sending an email or posting a notice on the Service console. The updated
                Tracker Agreement, Service Description will take effect on the date specified in the notification. If a
                modification is detrimental and negatively impacts your use of the Service (excluding the termination or
                modification of any beta feature or functionality), you may request to terminate this Tracker Boot
                Agreement by providing written notice within ten (10) days of the modification announcement to
                trackerboot@bekindlabs.com. Termination will take effect on the date we receive your request. In such
                cases, any prepaid subscription fees will be refunded on a prorated basis for the remainder of your
                Subscription Term. Your continued use of the Service after the effective date of any modification to the
                Tracker Agreement, the Service Description, and the Third Party Terms will be considered acceptance of
                the revised terms.
              </li>
            </ul>
          </div>
        </div>
      </div>
      <LandingFooter />
    </>
  )
}
